import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthShell from '../components/auth/AuthShell';
import EmailStep from '../components/auth/EmailStep';
import VerifyOtpStep from '../components/auth/VerifyOtpStep';
import NewPasswordStep from '../components/auth/NewPasswordStep';

const INITIAL = { step: 'email', email: '', codeExpiresAt: null, resendAt: null, resetToken: null, sessionExpiresAt: null };
const inMs = (seconds) => Date.now() + seconds * 1000;

/**
 * Forgot password: 1) email → 2) 6-digit code → 3) new password → back to login.
 *
 * Everything (email, reset token, timers) lives in this component's state only: never in
 * localStorage or the URL. A refresh therefore always starts again at step 1, and the
 * state is dropped when the admin leaves the page.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Email typed on the login page, handed over in router state (not the URL)
  const [flow, setFlow] = useState(() => ({ ...INITIAL, email: location.state?.email ?? '' }));

  const startAgain = () => setFlow((f) => ({ ...INITIAL, email: f.email }));

  let step;
  if (flow.step === 'verify') {
    step = (
      <VerifyOtpStep
        email={flow.email}
        codeExpiresAt={flow.codeExpiresAt}
        resendAt={flow.resendAt}
        onResent={({ expiresInSeconds, resendAfterSeconds }) =>
          setFlow((f) => ({ ...f, codeExpiresAt: inMs(expiresInSeconds), resendAt: inMs(resendAfterSeconds) }))
        }
        onVerified={({ resetToken, expiresInSeconds }) =>
          setFlow((f) => ({ ...f, step: 'reset', resetToken, sessionExpiresAt: inMs(expiresInSeconds) }))
        }
        onChangeEmail={startAgain}
      />
    );
  } else if (flow.step === 'reset') {
    step = (
      <NewPasswordStep
        resetToken={flow.resetToken}
        sessionExpiresAt={flow.sessionExpiresAt}
        onStartAgain={startAgain}
        onDone={(message) => {
          const { email } = flow;
          setFlow(INITIAL);
          toast.success(message || 'Password updated successfully. You can now log in');
          navigate('/login', { replace: true, state: { email } });
        }}
      />
    );
  } else {
    step = (
      <EmailStep
        key={flow.email}
        initialEmail={flow.email}
        onSent={({ email, message, expiresInSeconds, resendAfterSeconds }) => {
          toast.info(message || 'If an account exists for this email, we have sent a 6-digit code to it');
          setFlow({ ...INITIAL, step: 'verify', email, codeExpiresAt: inMs(expiresInSeconds), resendAt: inMs(resendAfterSeconds) });
        }}
      />
    );
  }

  return <AuthShell>{step}</AuthShell>;
}
